import { formatMoney } from '@/lib/format'
import { abs, add, cmp, div, money, mul, sign, sub, toChartNumber, type Money } from '@/lib/money'
import type { BrokerLean } from '@/schemas/broker'

/**
 * The rupee-scale formatters shared across Broker Chirfaar's flows.
 *
 * Root cause this file exists to remove: `@/features/market/api/marketMappers`
 * already has `rupeesToCrore`/`rupeesToArba`, but both are typed to return a
 * plain `string` — their only caller hands the result straight to a Zod
 * `.parse()`, which re-brands it, so the loose return type was never a
 * problem there. Called directly from a broker screen component, with no
 * schema in between, that plain string does not satisfy the branded `Money`
 * type `@/lib/money`'s Decimal helpers require. Three Wave 2 agents hit this
 * independently and each wrote their own Arba/Crore/Lakh division
 * (`components/broker/broker-format.ts`, `components/compare/compare-format.ts`,
 * `components/large-trades/format.ts`), and two more duplicated a simpler
 * two-tier Crore/Arba helper verbatim (`components/all-brokers/format.ts`,
 * `components/overview/format.ts`). This module is the one place that
 * division happens now, staying branded `Money` end to end — nothing here
 * calls `Number()` on a rupee figure directly or drops through `parseFloat`
 * (lint blocks the latter for exactly this reason).
 *
 * What stays local to a flow's own `*format*.ts` file: label/band lookup
 * tables with exactly one consumer flow (large-trades' size chips),
 * anything else with exactly one consumer flow (broker-format's
 * `formatMultiple`, `formatShares`, the window-partial caption), and
 * non-money copy helpers (reviews' author redaction). Only what at least two
 * flows genuinely need the same way lives here.
 *
 * The lean vocabulary moved up from `components/broker/broker-format.ts` for
 * exactly that reason: All brokers' `LeanBadge` had grown a second, differently
 * worded table of its own ("Bought more than sold" vs. "Bought") over the same
 * three-value enum. Only the short form is left: the badge that carried the
 * long one is gone with the chip it sat in (see `LEAN_LABEL`'s note below).
 */

/** What every formatter here prints when the backend sent null. */
export const EM_DASH = '—'

/** U+2212, matching `formatSigned` in `@/lib/format` — a real minus, not the
 *  ASCII hyphen `Intl` would emit, which reads narrower beside a figure. */
const MINUS = '−'

const ONE_LAKH = money('100000')
const ONE_CRORE = money('10000000')
const ONE_ARBA = money('1000000000')

export const rupeesToLakh = (value: Money): Money => div(value, 100_000)
export const rupeesToCrore = (value: Money): Money => div(value, 10_000_000)
export const rupeesToArba = (value: Money): Money => div(value, 1_000_000_000)

/**
 * The way back, for a figure a mapper already scaled (a sector's `turnoverCr`, the floorsheet's
 * `valueTradedArba`) so it can go through `formatRupees` like every other rupee figure instead of
 * a fixed-unit ladder of its own. Decimal multiplication, so the round trip is exact: 0.25 Cr comes
 * back as Rs 25,00,000 and prints "Rs 25.00 L", never "0.25 Cr".
 */
export const croreToRupees = (value: Money): Money => mul(value, 10_000_000)
export const arbaToRupees = (value: Money): Money => mul(value, 1_000_000_000)

interface ScaleStep {
  floor: Money
  toScale: (value: Money) => Money
  suffix: string
}

/**
 * Arba (10⁹) · Crore (10⁷) · Lakh (10⁵), the three units NEPSE readers use.
 * Ordered largest first and read with `cmp`, a Decimal comparison, so a
 * ten-digit turnover figure is never routed through a float to pick its unit.
 */
const SCALE: readonly ScaleStep[] = [
  { floor: ONE_ARBA, toScale: rupeesToArba, suffix: ' arba' },
  { floor: ONE_CRORE, toScale: rupeesToCrore, suffix: ' Cr' },
  { floor: ONE_LAKH, toScale: rupeesToLakh, suffix: ' L' },
]

/** A scaled figure and its unit apart: "4.82" and " arba". */
interface ScaledRupees {
  figure: string
  /** The unit with its leading space, or `''` below a lakh. */
  unit: string
}

/**
 * A magnitude (already unsigned — callers own the sign) auto-scaled to
 * Arba/Cr/L and Nepali-grouped, or printed whole below a lakh, where the
 * paisa are noise on a turnover-sized figure. The building block behind
 * `formatRupees` (Rs-prefixed) and `compactRupees` (bare).
 */
const scaleRupeeParts = (magnitude: Money): ScaledRupees => {
  const step = SCALE.find((candidate) => cmp(magnitude, candidate.floor) >= 0)
  return step === undefined
    ? { figure: formatMoney(magnitude, { whole: true }), unit: '' }
    : { figure: formatMoney(step.toScale(magnitude)), unit: step.suffix }
}

const scaleRupees = (magnitude: Money): string => {
  const { figure, unit } = scaleRupeeParts(magnitude)
  return `${figure}${unit}`
}

/**
 * A rupee figure at the scale a NEPSE reader uses it, currency-marked:
 * "Rs 4.82 arba", "Rs 12.40 Cr", "Rs 3.27 L", "Rs 8,420".
 *
 * Unsigned — a turnover has no direction. Use `formatSignedRupees` for a net.
 */
export const formatRupees = (value: Money | null): string =>
  value === null ? EM_DASH : `Rs ${scaleRupees(abs(value))}`

/**
 * `formatRupees` split where a line may break — "Rs 4.82" and " arba" — for a cell too narrow for
 * the whole figure: the caller keeps `figure` on one line and lets only `unit` wrap.
 */
export const formatRupeesParts = (value: Money): ScaledRupees => {
  const { figure, unit } = scaleRupeeParts(abs(value))
  return { figure: `Rs ${figure}`, unit }
}

/** The same, signed: "+Rs 1.20 Cr" / "−Rs 3.27 L". Net flow, net value. */
export const formatSignedRupees = (value: Money | null): string => {
  if (value === null) return EM_DASH
  const direction = sign(value)
  const body = `Rs ${scaleRupees(abs(value))}`
  return direction > 0 ? `+${body}` : direction < 0 ? `${MINUS}${body}` : body
}

/**
 * The same scale, with no "Rs " prefix — for a cell that already sits beside
 * a label naming the figure (a comparison column, a bar-list row).
 */
export const compactRupees = (value: Money | null | undefined): string => {
  if (value === null || value === undefined) return EM_DASH
  const body = scaleRupees(abs(value))
  return sign(value) < 0 ? `${MINUS}${body}` : body
}

/** The same, signed — for a net figure where the direction is the point. */
export const compactSignedRupees = (value: Money | null | undefined): string => {
  if (value === null || value === undefined) return EM_DASH
  return sign(value) > 0 ? `+${compactRupees(value)}` : compactRupees(value)
}

/**
 * A broker's turnover split back into its two legs — the live Figma's
 * All-brokers table (`4236:78726`) draws "Buy amount" and "Sell amount" as
 * their own columns, and `BrokerListRow` serves neither directly. It does not
 * need to: `turnover` is bought + sold (both legs, `listing.py:31`) and
 * `netTurnover` is bought − sold, signed — two real served figures, and this
 * is exact algebra on them, not an estimate:
 *
 *   buy  = (turnover + netTurnover) / 2
 *   sell = (turnover − netTurnover) / 2
 *
 * (`buy + sell === turnover` and `buy − sell === netTurnover` for any pair
 * the backend could send.) Kept branded `Money` end to end, like every other
 * derived figure in this file.
 */
export const brokerBuySellAmounts = (row: {
  turnover: Money
  netTurnover: Money
}): { buy: Money; sell: Money } => ({
  buy: div(add(row.turnover, row.netTurnover), 2),
  sell: div(sub(row.turnover, row.netTurnover), 2),
})

/**
 * REMOVED: `formatCroreOrArba`.
 *
 * It was a two-tier Arba/Crore ladder with NO lower bound, so every amount
 * below one crore was divided by 10⁷ and printed to two decimals: Rs 25,18,000
 * rendered "0.25 Cr" where the rest of the feature said "Rs 25.18 L", and
 * anything under Rs 50,000 collapsed to a flat "0.00 Cr". That was live — on
 * staging today every broker's turnover is under a crore, so every row of All
 * brokers and every bar on Overview read in hundredths of a crore.
 *
 * It is not fixed by bolting a Lakh step and a whole-rupee floor onto it,
 * because that makes it `scaleRupees` a second time. The three call sites
 * (`all-brokers/BrokerRankList`, `overview/BrokerValueBar`,
 * `overview/FloorStatsStrip`) now use `compactRupees` / `formatRupees`, which
 * already walk the full Arba · Crore · Lakh · whole-rupee ladder above.
 */

/** The tone a signed rupee figure wears. Zero stays flat, never green. */
export const signedTone = (
  value: Money,
): 'text-market-up' | 'text-market-down' | 'text-market-flat' => {
  const direction = sign(value)
  return direction > 0 ? 'text-market-up' : direction < 0 ? 'text-market-down' : 'text-market-flat'
}

/** The same for a plain count of shares, which is a number and not a Money. */
export const signedSharesTone = (
  units: number,
): 'text-market-up' | 'text-market-down' | 'text-market-flat' =>
  units > 0 ? 'text-market-up' : units < 0 ? 'text-market-down' : 'text-market-flat'

/**
 * The `Badge` tone for the same sign — `signedTone`'s twin for a chip that wraps a `<Change>`.
 * Zero is `neutral`, never green: an unchanged row is not a gain.
 */
export const signedBadgeTone = (value: Money): 'up' | 'down' | 'neutral' => {
  const direction = sign(value)
  return direction > 0 ? 'up' : direction < 0 ? 'down' : 'neutral'
}

/**
 * Which way a flow leaned, in the short form a dense stock table has room for.
 *
 * This is the `bought | sold | balanced` enum (`BrokerListRow`,
 * `BrokerStockFlow`), never large-trades' `buying | selling | balanced` —
 * two vocabularies on purpose, see `@/schemas/broker`.
 */
export const LEAN_LABEL: Record<BrokerLean, string> = {
  bought: 'Bought',
  sold: 'Sold',
  balanced: 'Balanced',
}

/**
 * REMOVED: `LEAN_LABEL_LONG` (and the `LeanBadge` it backed).
 *
 * The live design's All-brokers table (`4236:78726`) has no lean chip under
 * the turnover figure at all — Broker / Turnover today / Buy amount / Sell
 * amount, and nothing else in that cell — so the badge was reading a fact
 * the two new Buy/Sell columns already show as real numbers. Owner call,
 * from a screenshot of the live table: "there is no sold more than bought
 * kinda chips on that table. remove those." `LeanBadge.tsx` had exactly one
 * caller (`TurnoverCell.tsx`, also removed); the short `LEAN_LABEL`/
 * `LEAN_TONE` pair below stays — `StockFlowCells.tsx`'s `LeaningBadge` still
 * needs it for By broker's Net-position column, which the design does draw
 * the badge on.
 */

/** The Badge tone each lean wears. Never colour alone — the label says it too. */
export const LEAN_TONE: Record<BrokerLean, 'up' | 'down' | 'neutral'> = {
  bought: 'up',
  sold: 'down',
  balanced: 'neutral',
}

/** A bare ratio or index figure: "3.01", "1.24". Not a rating — see below. */
export const formatRatio = (value: Money | null | undefined): string =>
  value === null || value === undefined ? EM_DASH : formatMoney(value)

/**
 * A rating out of five, at the ONE decimal every frame that draws one writes
 * ("★ 4.4"): All brokers' rows, the By-broker header, Compare's "User rating"
 * row and the Reviews rail all agree (`docs/broker-chirfaar/00-index.md`).
 *
 * `formatMoney`'s whole/paisa split cannot express that — it prints "4.40" —
 * so a rating never goes through it, and never through `formatRatio` either.
 * That was the bug this helper removes: `StarRating` formatted a rating
 * correctly while `RatingSummary` (`formatMoney`) and `CompareNumbers`
 * (`formatRatio`) each formatted the same number their own way, so one house
 * rated 4.4 printed "4.4" in a table and "4.40" on its reviews page.
 *
 * `toChartNumber` is the sanctioned Money→number step for direct-to-text
 * formatting like this; nothing here feeds the number back into a stored
 * value or a further calculation, and `parseFloat` is never involved.
 *
 * Takes a non-null `Money` on purpose: "no rating yet" is not a number, and
 * each surface words that state differently ("—", "Not rated yet"), so the
 * null copy stays at the call site rather than being decided here.
 */
export const formatRating = (value: Money): string => toChartNumber(value).toFixed(1)

/**
 * An unsigned percentage a share/lean stat already computed: "12.40%".
 *
 * Deliberately not `formatPercent` from `@/lib/format`, which signs its
 * output — a market share is a share of something, not a change in it, and
 * "+12.40%" would read as a gain.
 */
export const formatPercentValue = (value: Money | null | undefined): string =>
  value === null || value === undefined ? EM_DASH : `${formatMoney(value)}%`

/**
 * The same share rounded to whole percent — "62%" — for a label ANNOTATING a
 * bar that already carries the magnitude.
 *
 * The rule this pair encodes, after four different precisions turned up for
 * one kind of quantity across the feature: a percentage that is the only
 * representation of the number gets `formatPercentValue`'s two decimals;
 * a percentage sitting beside a bar or a track that shows the same thing
 * gets this one, because the bar is already saying it to within a percent
 * and two decimals beside it read as false precision. Sector flow's "Bought
 * share" and the reviews histogram are the only two call sites.
 */
export const formatPercentBarLabel = (value: Money | null | undefined): string =>
  value === null || value === undefined ? EM_DASH : `${formatMoney(value, { whole: true })}%`
